import {
  APP_INITIALIZE,
  FILTER_CHANGE,
  FILTERS_MOUNTED,
  FILTER_RESET,
  QUERY_CHANGE,
  SEARCH_SUCCESS,
} from "actions/const";
import * as queryUtils from "utils/query";
import { FILTER_TYPES } from "services/search/const";
import * as filterUtils from "utils/filter";
import { APP_FILTER_TYPES, FACET_PATH_DELIMITER } from "constants/filter";
import config from "config";

/**
 * builds data for given facet
 *
 * @param {Object} levelFacets results facet or pathpath level info
 * @param {Array} levelValues values for current facet or facet level in case of facet-path
 * @returns {Object} data for given facetFilter
 */
function buildFacetData(levelFacets, levelValues = []) {
  const missingValues = new Set(levelValues);
  const data = levelFacets
    ? levelFacets.map((entry) => {
        missingValues.delete(entry.value);
        return {
          count: entry.count,
          value: entry.value,
          label: entry.label || entry.value,
        };
      })
    : [];

  if (missingValues.size > 0) {
    for (let value of missingValues) {
      data.push({
        count: 0,
        label: value,
        value,
      });
    }
  }
  return data;
}

/**
 * build state for facet filter based on given actual filterstate and results facet
 *
 * @param {Object} filterState current filterstate for filter
 * @param {Array} filterFacet results facet entries for filter
 * @returns {data: {}, values: []} new filterstate
 */
function buildFacetFilter(filterState, filterFacet) {
  const values = filterState && filterState.values ? filterState.values : [];
  const data = buildFacetData(filterFacet, values);

  return {
    ...filterState,
    data,
    values,
    salType: FILTER_TYPES.FACET,
  };
}

/**
 * updates the given data with leveldata, building a recursive data strucuture
 *
 * @param {[{value: String, label: String, count: Number}]} data data (of state) to update
 * @param {Number} level target level to update
 * @param {Array} selectedValuePath the selected values for FacetPath
 * @param {[{value: String, label: String, count: Number}]} levelData the leveldata to instert -- see buildFacetData
 * @param {Number} currentLevel=0 level for recursion
 * @returns {Array} the updated data
 */
function updateFacetPathLevelData(data, level, selectedValuePath, levelData) {
  if (level === 0) {
    return levelData;
  }

  filterUtils.traverseFacepathSelection(
    data,
    selectedValuePath,
    (entry) => {
      entry.data = levelData;
      return data;
    },
    level,
  );
  return data;
}

function buildLevelFacets(selectedValuePath, level, levelFacets) {
  const filterPrefix =
    level > 0
      ? selectedValuePath.slice(0, level).join(FACET_PATH_DELIMITER) +
        FACET_PATH_DELIMITER
      : "";

  return levelFacets
    .filter((entry) => {
      return entry.value.startsWith(filterPrefix);
    })
    .map((entry) => {
      return {
        ...entry,
        value: entry.value.slice(filterPrefix.length),
      };
    });
}

function buildFacetPathFilter(filterState, filterFPath) {
  const values = filterState && filterState.values ? filterState.values : [];
  let data = [];

  if (filterFPath) {
    const selectedValue = values[0];
    const selectedValuePath = filterUtils.facetPathValueToArray(selectedValue);

    data = filterFPath.reduce((accumulator, levelFacets, index) => {
      levelFacets = buildLevelFacets(selectedValuePath, index, levelFacets);
      const levelValues = selectedValuePath[index]
        ? [selectedValuePath[index]]
        : [];

      return updateFacetPathLevelData(
        accumulator,
        index,
        selectedValuePath,
        buildFacetData(levelFacets, levelValues),
      );
    }, data);
  }

  return {
    ...filterState,
    data,
    values,
    salType: FILTER_TYPES.FACET_PATH,
  };
}

/**
 * builds query filter state
 *
 * @param {Object} state filters application state
 * @param {salLib.Result} result the search result
 * @returns {Object} literal with data for query filter
 */
function buildQueryFilter(state) {
  const name = filterUtils.getQueryFilterName();
  const filter = {};

  if (!state[name]) {
    return filter;
  }

  filter[name] = {
    ...state[name],
    salType: FILTER_TYPES.QUERY,
  };

  return filter;
}

/**
 * builds data for all filters of type facet for given searchresult
 *
 * @param {Object} state filters applicaton state
 * @param {salLib.Result} result the search result
 * @returns {Object} literal with data for every filter as property
 */
function buildFacetFilters(state, result) {
  const resultFacets = result.facets;
  const facetFilters = filterUtils.getFiltersByType(FILTER_TYPES.FACET);
  const filters = {};

  for (let name in facetFilters) {
    let fieldName = facetFilters[name].field;
    let filterFacet = resultFacets ? resultFacets[fieldName] : undefined;
    let filterState = state[name];

    filters[name] = buildFacetFilter(filterState, filterFacet);
  }
  return filters;
}

/**
 * builds data for all filters fo type facetpath for given searchresult
 *
 * @param {Object} state filters application state
 * @param {salLib.Result} result the search result
 * @returns {Object} literal wit data for every filter as property
 */
function buildFacetPathFilters(state, result) {
  const resultFpaths = result.facetpaths;
  const facetPathFilters = filterUtils.getFiltersByType(
    FILTER_TYPES.FACET_PATH,
  );
  const filters = {};

  for (let name in facetPathFilters) {
    let fieldName = facetPathFilters[name].field;
    let filterFPath = resultFpaths ? resultFpaths[fieldName] : undefined;
    let filterState = state[name];

    filters[name] = buildFacetPathFilter(filterState, filterFPath);
  }
  return filters;
}

/**
 * extracts the filters relevant data for given result.
 * in terms of facet based filters this are the relevant facet values.
 *
 * @param {Object} state filters applicaton state
 * @param {salLib.Result} result
 * @returns {Object} object of kind {filtername: data}
 */
function buildFilters(state, result) {
  const queryFilter = buildQueryFilter(state, result);
  const facetFilters = buildFacetFilters(state, result);
  const facetPathFilters = buildFacetPathFilters(state, result);

  return {
    ...queryFilter,
    ...facetFilters,
    ...facetPathFilters,
  };
}

/**
 * resets all filters in current state except Search Queries
 *
 * @param {Object} state current state with filters
 * @returns {Object} with resetted filter values
 */
function buildResetFilterState(state, isQueryChange) {
  const filterKeys = Object.keys(state) || [];

  return filterKeys.reduce((accumulator, key) => {
    const filter = state[key];

    if (
      !filter.filterType ||
      filter.filterType === FILTER_TYPES.QUERY ||
      (!isQueryChange && !filter.isMounted)
    ) {
      return accumulator;
    }

    accumulator[key] = Object.assign(
      {},
      filter,
      {
        values:
          filter.defaultValues && !isQueryChange ? filter.defaultValues : [],
      },
      filter.filterType === APP_FILTER_TYPES.CASCADE
        ? { currentValues: filter.defaultValues }
        : {},
    );

    return accumulator;
  }, {});
}

/**
 * the filter reducer, that handles all filter relevant actions
 *
 * @param {Object} state=initialState
 * @param {{type:{string}, ...}} action
 */
function reducer(state = config.state.filters, action) {
  let filters;
  let filterName;

  switch (action.type) {
    case APP_INITIALIZE:
      const urlFilters = queryUtils.toFiltersLiteral(action.urlSearch);
      const urlFilterObj = Object.keys(urlFilters).reduce(
        (accumulator, fName) => {
          accumulator[fName] = {
            ...state[fName],
            ...urlFilters[fName],
          };
          return accumulator;
        },
        {},
      );

      const defaultValues = Object.keys(urlFilterObj).reduce(
        (accumulator, fName) => {
          if (fName !== filterUtils.getQueryFilterName()) {
            accumulator[fName] = {
              ...state[fName],
              ...urlFilterObj[fName],
              defaultValues:
                state[fName] && state[fName].values ? state[fName].values : [],
            };
          } else {
            accumulator[fName] = {
              ...state[fName],
              ...urlFilterObj[fName],
            };
          }

          return accumulator;
        },
        {},
      );

      return {
        ...state,
        ...defaultValues,
      };

    case QUERY_CHANGE:
      filterName = action.name;
      const value = action.value ? action.value : "";

      return {
        ...state,
        ...buildResetFilterState(state, true),
        [filterName]: {
          ...state[filterName],
          values: [value],
          valueBeforeSuggest: action.valueBeforeSuggest,
        },
      };

    case FILTER_CHANGE:
      filterName = action.name;
      const values = action.values ? action.values : [];
      const data =
        state[filterName] && state[filterName].data
          ? state[filterName].data
          : [];
      const currentValues =
        state[filterName] &&
        state[filterName].filterType === APP_FILTER_TYPES.CASCADE
          ? { currentValues: values }
          : {};

      return {
        ...state,
        [filterName]: {
          ...state[filterName],
          values,
          data,
          ...currentValues,
        },
      };

    case FILTERS_MOUNTED:
      const newFilters = Object.keys(action.filters).reduce(
        (accumulator, filterKey) => {
          const filter = state[filterKey];

          accumulator[filterKey] = { ...filter, ...action.filters[filterKey] };
          return accumulator;
        },
        {},
      );

      return {
        ...state,
        ...newFilters,
      };

    case FILTER_RESET:
      return {
        ...state,
        ...buildResetFilterState(state),
      };

    case SEARCH_SUCCESS:
      filters = buildFilters(state, action.result);

      return {
        ...state,
        ...filters,
      };

    default:
      return state;
  }
}
export default reducer;
